import React from 'react';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { cn } from '@/lib/cn';
import { Input } from '@/components/ui/input';
import { L } from './field-label';
import type { AdditionalCharge, ComputedAdditionalCharge, ServiceFormValues } from './types';

interface PricingFeeSectionsProps {
  register: UseFormRegister<ServiceFormValues>;
  setValue: UseFormSetValue<ServiceFormValues>;
  watch: UseFormWatch<ServiceFormValues>;
  errors: FieldErrors<ServiceFormValues>;
  setRupees: (field: 'defaultApplicationFeeRupees' | 'defaultServiceChargeRupees' | 'governmentFeeRupees', raw: string) => void;
  assocGstAmountNum: number;
  totalAssocFee: string;
  platformGstAmountNum: number;
  totalPlatformFee: string;
  additionalCharges: AdditionalCharge[];
  setAdditionalCharges: React.Dispatch<React.SetStateAction<AdditionalCharge[]>>;
  computedAddlCharges: ComputedAdditionalCharge[];
  grandTotalSubtotal: number;
  grandTotalGst: number;
  grandTotalAmount: number;
  onShowOverviewPopup: () => void;
}

export function PricingFeeSections({
  register,
  setValue,
  watch,
  errors,
  setRupees,
  assocGstAmountNum,
  totalAssocFee,
  platformGstAmountNum,
  totalPlatformFee,
  additionalCharges,
  setAdditionalCharges,
  computedAddlCharges,
  grandTotalSubtotal,
  grandTotalGst,
  grandTotalAmount,
  onShowOverviewPopup,
}: PricingFeeSectionsProps) {
  const isStartsFrom = watch('isStartsFrom');

  return (
    <>
      {/* Pricing Mode Section */}
      <div className="space-y-3 rounded-md border border-brand-200 bg-brand-50/20 p-3.5">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            {...register('isStartsFrom')}
            className="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-500"
          />
          <span className="font-semibold text-gray-900 text-sm">
            Service Starts From (₹)
          </span>
        </label>
        {isStartsFrom && (
          <div className="mt-2">
            <L label="Starts From Amount (₹) *">
              <Input
                inputMode="decimal"
                {...register('startsFromRupees')}
                onChange={(e) => setValue('startsFromRupees', e.target.value.replace(/[^0-9.]/g, ''))}
                onFocus={(e) => e.target.select()}
                placeholder="1000.00"
              />
            </L>
          </div>
        )}
      </div>

      {/* Associate Fee Section */}
      <div className={cn('space-y-3 rounded-md border border-gray-200 bg-emerald-50/20 p-3.5', isStartsFrom && 'opacity-50 pointer-events-none')}>
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <span className="font-semibold text-gray-900 text-xs uppercase tracking-wide">
            Associate Fee Section {isStartsFrom ? '(Disabled for Starts From)' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <L label="Associate fee (₹)">
            <Input
              inputMode="decimal"
              {...register('defaultApplicationFeeRupees')}
              onChange={(e) => setRupees('defaultApplicationFeeRupees', e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const val = watch('defaultApplicationFeeRupees');
                if (!val || val === '') setValue('defaultApplicationFeeRupees', '0');
              }}
              placeholder="0.00"
              disabled={isStartsFrom}
            />
          </L>
          <L label="GST %">
            <Input
              inputMode="decimal"
              {...register('associateGstPercentage')}
              onChange={(e) => setValue('associateGstPercentage', e.target.value.replace(/[^0-9.]/g, ''))}
              onFocus={(e) => e.target.select()}
              placeholder="18.00"
              disabled={isStartsFrom}
            />
          </L>
          <L label="GST Amount (₹)">
            <Input
              value={assocGstAmountNum.toFixed(2)}
              readOnly
              disabled
              className="bg-gray-100 font-medium text-gray-700 cursor-not-allowed"
            />
          </L>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L label="HSN / SAC *">
            <Input
              {...register('associateHsnSacCode', {
                required: 'Associate HSN / SAC code is required',
              })}
              onFocus={(e) => e.target.select()}
              placeholder="998213"
              disabled={isStartsFrom}
              className={cn(errors.associateHsnSacCode && 'border-red-500 focus:border-red-600 focus:ring-red-500/30')}
            />
            {errors.associateHsnSacCode ? <p className="mt-1 text-xs font-medium text-red-600">{errors.associateHsnSacCode.message}</p> : null}
          </L>
          <L label="Total Associate fee (incl. GST ₹)">
            <Input
              value={totalAssocFee}
              readOnly
              disabled
              className="bg-gray-100 font-semibold text-emerald-800 cursor-not-allowed"
            />
          </L>
        </div>
      </div>

      {/* Platform Fee Section */}
      <div className={cn('space-y-3 rounded-md border border-gray-200 bg-blue-50/20 p-3.5', isStartsFrom && 'opacity-50 pointer-events-none')}>
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <span className="font-semibold text-gray-900 text-xs uppercase tracking-wide">
            Platform Fee Section {isStartsFrom ? '(Disabled for Starts From)' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <L label="Platform fee (₹)">
            <Input
              inputMode="decimal"
              {...register('defaultServiceChargeRupees')}
              onChange={(e) => setRupees('defaultServiceChargeRupees', e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const val = watch('defaultServiceChargeRupees');
                if (!val || val === '') setValue('defaultServiceChargeRupees', '0');
              }}
              placeholder="0.00"
              disabled={isStartsFrom}
            />
          </L>
          <L label="GST %">
            <Input
              inputMode="decimal"
              {...register('platformGstPercentage')}
              onChange={(e) => setValue('platformGstPercentage', e.target.value.replace(/[^0-9.]/g, ''))}
              onFocus={(e) => e.target.select()}
              placeholder="18.00"
              disabled={isStartsFrom}
            />
          </L>
          <L label="GST Amount (₹)">
            <Input
              value={platformGstAmountNum.toFixed(2)}
              readOnly
              disabled
              className="bg-gray-100 font-medium text-gray-700 cursor-not-allowed"
            />
          </L>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L label="HSN / SAC *">
            <Input
              {...register('platformHsnSacCode', {
                required: 'Platform HSN / SAC code is required',
              })}
              onFocus={(e) => e.target.select()}
              placeholder="998213"
              disabled={isStartsFrom}
              className={cn(errors.platformHsnSacCode && 'border-red-500 focus:border-red-600 focus:ring-red-500/30')}
            />
            {errors.platformHsnSacCode ? <p className="mt-1 text-xs font-medium text-red-600">{errors.platformHsnSacCode.message}</p> : null}
          </L>
          <L label="Total Platform fee (incl. GST ₹)">
            <Input
              value={totalPlatformFee}
              readOnly
              disabled
              className="bg-gray-100 font-semibold text-blue-800 cursor-not-allowed"
            />
          </L>
        </div>
      </div>

      <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-3', isStartsFrom && 'opacity-50 pointer-events-none')}>
        <L label="Assoc split %">
          <Input
            {...register('defaultAssociateSplitPercentage')}
            onFocus={(e) => e.target.select()}
            disabled={isStartsFrom}
          />
        </L>
      </div>

      {/* Additional Charges Section */}
      <div className={cn('rounded-md border border-gray-200 bg-gray-50/60 p-3.5 space-y-3', isStartsFrom && 'opacity-50 pointer-events-none')}>
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <p className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
            Additional Charges {isStartsFrom ? '(Disabled for Starts From)' : ''}
          </p>
          {!isStartsFrom && (
            <button
              type="button"
              onClick={() =>
                setAdditionalCharges((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), name: '', amountRupees: '', gstPercentage: '18.00', hsnSacCode: '998213' },
                ])
              }
              className="text-xs font-semibold text-brand-700 hover:underline"
            >
              + Additional charge
            </button>
          )}
        </div>

        {additionalCharges.length === 0 ? (
          <div className="py-2 text-xs text-gray-500 text-center">
            {!isStartsFrom ? (
              <>
                Click{' '}
                <button
                  type="button"
                  onClick={() =>
                    setAdditionalCharges([{ id: crypto.randomUUID(), name: '', amountRupees: '', gstPercentage: '18.00', hsnSacCode: '998213' }])
                  }
                  className="font-medium text-brand-700 hover:underline"
                >
                  + Additional charge
                </button>{' '}
                to add custom fee items.
              </>
            ) : (
              'Disabled when Service Starts From is active.'
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {computedAddlCharges.map((chg, idx) => (
              <div key={chg.id} className="rounded-md border border-gray-200 bg-white p-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-32 shrink-0">
                    <L label="Amount (₹)">
                      <Input
                        inputMode="decimal"
                        value={chg.amountRupees}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          setAdditionalCharges((prev) =>
                            prev.map((c, i) => (i === idx ? { ...c, amountRupees: val } : c))
                          );
                        }}
                        placeholder="0.00"
                        disabled={isStartsFrom}
                        className="text-xs"
                      />
                    </L>
                  </div>
                  <div className="flex-1">
                    <L label="Description / Charge Name">
                      <Input
                        value={chg.name}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAdditionalCharges((prev) =>
                            prev.map((c, i) => (i === idx ? { ...c, name: val } : c))
                          );
                        }}
                        placeholder="e.g. Stamp duty, Govt fee"
                        disabled={isStartsFrom}
                        className="text-xs"
                      />
                    </L>
                  </div>
                  {!isStartsFrom && (
                    <button
                      type="button"
                      onClick={() => setAdditionalCharges((prev) => prev.filter((_, i) => i !== idx))}
                      className="self-end mb-1 text-xs font-bold text-red-600 hover:underline px-2 py-1.5"
                      title="Remove charge"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-2 pt-1">
                  <L label="GST %">
                    <Input
                      inputMode="decimal"
                      value={chg.gstPercentage}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setAdditionalCharges((prev) =>
                          prev.map((c, i) => (i === idx ? { ...c, gstPercentage: val } : c))
                        );
                      }}
                      placeholder="18.00"
                      disabled={isStartsFrom}
                      className="text-xs"
                    />
                  </L>
                  <L label="GST Amount (₹)">
                    <Input
                      value={chg.gstAmount}
                      readOnly
                      disabled
                      className="bg-gray-100 text-xs text-gray-700 cursor-not-allowed"
                    />
                  </L>
                  <L label="HSN / SAC">
                    <Input
                      value={chg.hsnSacCode}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAdditionalCharges((prev) =>
                          prev.map((c, i) => (i === idx ? { ...c, hsnSacCode: val } : c))
                        );
                      }}
                      placeholder="998213"
                      disabled={isStartsFrom}
                      className="text-xs"
                    />
                  </L>
                  <L label="Total Amount (₹)">
                    <Input
                      value={chg.totalAmount}
                      readOnly
                      disabled
                      className="bg-gray-100 font-semibold text-xs text-brand-800 cursor-not-allowed"
                    />
                  </L>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Government Fee Section */}
      <div className={cn('space-y-3 rounded-md border border-gray-200 bg-amber-50/20 p-3.5', isStartsFrom && 'opacity-50 pointer-events-none')}>
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <span className="font-semibold text-gray-900 text-xs uppercase tracking-wide">
            Government Fee Section {isStartsFrom ? '(Disabled for Starts From)' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <L label="Government fee (₹)">
            <Input
              inputMode="decimal"
              {...register('governmentFeeRupees')}
              onChange={(e) => setRupees('governmentFeeRupees', e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                const val = watch('governmentFeeRupees');
                if (!val || val === '') setValue('governmentFeeRupees', '0');
              }}
              placeholder="0.00"
              disabled={isStartsFrom}
            />
          </L>
        </div>
      </div>

      {/* Overall Summary Card */}
      <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Overall Summary</span>
            <button
              type="button"
              onClick={onShowOverviewPopup}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 hover:text-brand-800 bg-white hover:bg-brand-100/60 border border-brand-200 px-2 py-0.5 rounded transition-colors shadow-sm"
            >
              <span>Overview</span>
              <svg className="w-3 h-3 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-x-4">
            <span>Subtotal: <strong>₹{grandTotalSubtotal.toFixed(2)}</strong></span>
            <span>GST Total: <strong>₹{grandTotalGst.toFixed(2)}</strong></span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs text-gray-500 font-medium block">Overall Total Amount (incl. GST)</span>
          <span className="text-xl font-bold text-brand-800">₹{grandTotalAmount.toFixed(2)}</span>
        </div>
      </div>
    </>
  );
}
